
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PageHeader, LoadingState, EmptyState } from "@/components/features/states";
import { toast } from "sonner";
import {
  Wallet as WalletIcon,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowRightLeft,
  Package,
  Bike,
  Store,
  CreditCard,
  Banknote,
} from "lucide-react";
import type { Wallet, WalletTransaction, WalletType } from "@/lib/types";

const WALLET_META: Record<
  WalletType,
  { icon: any; color: string; bg: string; labelKey: string; category: "debit" | "credit" }
> = {
  // Debit wallets = prepaid balance you've loaded (you spend from these).
  sender: { icon: Package, color: "text-primary", bg: "bg-primary/10", labelKey: "wallet.sender", category: "debit" },
  // Credit wallets = earnings owed to you (paid out later).
  courier: { icon: Bike, color: "text-amber-600", bg: "bg-amber-500/10", labelKey: "wallet.courier", category: "credit" },
  provider: { icon: Store, color: "text-emerald-600", bg: "bg-emerald-500/10", labelKey: "wallet.provider", category: "credit" },
};

export function WalletView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedType, setSelectedType] = useState<WalletType>("sender");
  const [transferOpen, setTransferOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["wallets"],
    queryFn: () => api.wallet.me(),
  });
  const wallets: Wallet[] = data?.data?.wallets ?? [];

  const { data: txData } = useQuery({
    queryKey: ["wallet-transactions", selectedType],
    queryFn: () => api.wallet.transactions(selectedType),
    enabled: wallets.length > 0,
  });
  const transactions: WalletTransaction[] = txData?.data?.transactions ?? [];

  const selectedWallet = wallets.find((w) => w.type === selectedType);

  return (
    <div className="space-y-5">
      <PageHeader title={t("wallet.title")} subtitle={t("wallet.subtitle")} />

      {isLoading ? (
        <LoadingState label={t("wallet.loading")} />
      ) : (
        <>
          {/* Debit Wallets — prepaid balance you can spend. */}
          <WalletCategory
            title={t("wallet.debitWallets") || "Debit Wallets"}
            desc={t("wallet.debitDesc") || "Prepaid balance — money you've loaded to spend on deliveries"}
            icon={CreditCard}
            accent="text-primary"
          >
            <div className="grid gap-3">
              {(Object.keys(WALLET_META) as WalletType[])
                .filter((type) => WALLET_META[type].category === "debit")
                .map((type) => (
                  <WalletCard
                    key={type}
                    type={type}
                    wallet={wallets.find((x) => x.type === type)}
                    selected={selectedType === type}
                    onSelect={() => setSelectedType(type)}
                    t={t}
                  />
                ))}
            </div>
          </WalletCategory>

          {/* Credit Wallets — earnings owed to you. */}
          <WalletCategory
            title={t("wallet.creditWallets") || "Credit Wallets"}
            desc={t("wallet.creditDesc") || "Earnings owed to you — payout on demand or per schedule"}
            icon={Banknote}
            accent="text-emerald-600"
          >
            <div className="grid gap-3">
              {(Object.keys(WALLET_META) as WalletType[])
                .filter((type) => WALLET_META[type].category === "credit")
                .map((type) => (
                  <WalletCard
                    key={type}
                    type={type}
                    wallet={wallets.find((x) => x.type === type)}
                    selected={selectedType === type}
                    onSelect={() => setSelectedType(type)}
                    t={t}
                  />
                ))}
            </div>
          </WalletCategory>

          {/* Transfer button */}
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setTransferOpen(!transferOpen)}
          >
            <ArrowRightLeft className="mr-2 h-4 w-4" /> {t("wallet.transferToSender")}
          </Button>

          {transferOpen && (
            <TransferForm
              wallets={wallets}
              onClose={() => setTransferOpen(false)}
              onSuccess={() => {
                qc.invalidateQueries({ queryKey: ["wallets"] });
                qc.invalidateQueries({ queryKey: ["wallet-transactions"] });
              }}
            />
          )}

          {/* Transaction history */}
          {selectedWallet && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">
                  {t(WALLET_META[selectedType].labelKey)} — {t("wallet.transactions")}
                </p>
              </div>
              {transactions.length === 0 ? (
                <EmptyState title={t("wallet.noTransactions")} />
              ) : (
                <div className="max-h-96 space-y-1.5 overflow-y-auto scroll-area">
                  {transactions.map((tx) => (
                    <TransactionRow key={tx.id} tx={tx} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
// WalletCategory — labelled group of wallet cards.
// ============================================================
function WalletCategory({
  title,
  desc,
  icon: Icon,
  accent,
  children,
}: {
  title: string;
  desc: string;
  icon: any;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2.5">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

// ============================================================
// WalletCard — single wallet row.
// ============================================================
function WalletCard({
  type,
  wallet,
  selected,
  onSelect,
  t,
}: {
  type: WalletType;
  wallet: Wallet | undefined;
  selected: boolean;
  onSelect: () => void;
  t: (key: string) => string;
}) {
  const meta = WALLET_META[type];
  const Icon = meta.icon;
  return (
    <Card
      className={`cursor-pointer transition hover:shadow-md ${
        selected ? "border-primary ring-1 ring-primary/30" : ""
      }`}
      onClick={onSelect}
    >
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${meta.bg}`}>
          <Icon className={`h-6 w-6 ${meta.color}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{t(meta.labelKey)}</p>
          <p className="text-xs text-muted-foreground">{t(`wallet.${type}Desc`)}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold">{(wallet?.balance ?? 0).toLocaleString()} Fdj</p>
          {wallet && wallet.pendingBalance > 0 && (
            <p className="text-[10px] text-amber-600">
              +{wallet.pendingBalance.toLocaleString()} {t("wallet.pending")}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TransactionRow({ tx }: { tx: WalletTransaction }) {
  const { t } = useTranslation();
  const isCredit = tx.type === "credit" || tx.type === "transfer_in";
  const Icon = isCredit ? ArrowDownToLine : ArrowUpFromLine;
  const color = isCredit ? "text-emerald-600" : "text-red-600";
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-border bg-card p-2.5">
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isCredit ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium">{tx.description || t(`wallet.txType.${tx.type}`)}</p>
        <p className="text-[10px] text-muted-foreground">
          {new Date(tx.createdAt).toLocaleString()}
        </p>
      </div>
      <span className={`text-sm font-bold ${color}`}>
        {isCredit ? "+" : "-"}{tx.amount.toLocaleString()} Fdj
      </span>
    </div>
  );
}

function TransferForm({
  wallets,
  onClose,
  onSuccess,
}: {
  wallets: Wallet[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [from, setFrom] = useState<WalletType>("courier");
  const [amount, setAmount] = useState("");

  const courierWallet = wallets.find((w) => w.type === "courier");
  const providerWallet = wallets.find((w) => w.type === "provider");
  const fromWallet = from === "courier" ? courierWallet : providerWallet;

  const transferMutation = useMutation({
    mutationFn: (body: { from: "courier" | "provider"; to: "sender"; amount: number }) =>
      api.wallet.transfer(body),
    onSuccess: () => {
      toast.success(t("wallet.transferSuccess"));
      setAmount("");
      onSuccess();
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || t("wallet.transferFailed")),
  });

  const amt = parseFloat(amount) || 0;
  const canTransfer = amt > 0 && fromWallet && amt <= fromWallet.balance;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <p className="text-sm font-semibold">{t("wallet.transferTitle")}</p>
        <p className="text-xs text-muted-foreground">{t("wallet.transferHint")}</p>
        <div className="space-y-1.5">
          <Label>{t("wallet.from")}</Label>
          <select
            value={from}
            onChange={(e) => setFrom(e.target.value as WalletType)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
          >
            {courierWallet && courierWallet.balance > 0 && (
              <option value="courier">{t("wallet.courier")} ({courierWallet.balance.toLocaleString()} Fdj)</option>
            )}
            {providerWallet && providerWallet.balance > 0 && (
              <option value="provider">{t("wallet.provider")} ({providerWallet.balance.toLocaleString()} Fdj)</option>
            )}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("wallet.amount")} (Fdj)</Label>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            className="flex-1"
            disabled={!canTransfer || transferMutation.isPending}
            onClick={() => transferMutation.mutate({ from: from as "courier" | "provider", to: "sender", amount: amt })}
          >
            {transferMutation.isPending ? t("common.submitting") : t("wallet.transfer")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
